import { Controller } from "@hotwired/stimulus";
import WaveSurfer from "wavesurfer.js";

export default class extends Controller {
  static targets = ["fileInput", "durationField", "fileLabel", "dropZone", "zoneTitle", "previewContainer"];

  disconnect() {
    this.#destroyPreview();
  }

  // ── File input change ─────────────────────────────────────────────────────

  readMetadata() {
    const file = this.fileInputTarget.files[0];
    if (!file) return;
    this.#processFile(file);
  }

  // ── Drag / Drop ───────────────────────────────────────────────────────────

  dragOver(event) {
    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
    this.dropZoneTarget.classList.add("upload-zone--drag");
    if (this.hasZoneTitleTarget) this.zoneTitleTarget.textContent = "Drop to upload";
  }

  dragLeave(event) {
    // Only fire when leaving the zone itself, not a child element
    if (this.dropZoneTarget.contains(event.relatedTarget)) return;
    this.dropZoneTarget.classList.remove("upload-zone--drag");
    this.#resetZoneTitle();
  }

  drop(event) {
    event.preventDefault();
    this.dropZoneTarget.classList.remove("upload-zone--drag");
    this.#resetZoneTitle();

    const file = event.dataTransfer.files[0];
    if (!file || !file.type.startsWith("audio/")) return;

    // Assign to the file input so it submits with the form
    const dt = new DataTransfer();
    dt.items.add(file);
    this.fileInputTarget.files = dt.files;

    this.#processFile(file);
  }

  // ── Core ──────────────────────────────────────────────────────────────────

  #processFile(file) {
    // Update label
    if (this.hasFileLabelTarget) this.fileLabelTarget.textContent = file.name;
    if (this.hasZoneTitleTarget) this.zoneTitleTarget.textContent = "Replace audio";

    // Read duration
    const blobUrl = URL.createObjectURL(file);
    const audio = new Audio();

    audio.addEventListener(
      "loadedmetadata",
      () => {
        if (this.hasDurationFieldTarget) {
          this.durationFieldTarget.value = Math.round(audio.duration);
        }
        this.#renderPreview(blobUrl, file.name, audio.duration);
      },
      { once: true },
    );

    audio.addEventListener("error", () => URL.revokeObjectURL(blobUrl), { once: true });
    audio.src = blobUrl;
  }

  #renderPreview(blobUrl, filename, duration) {
    this.#destroyPreview();

    const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

    // Build the same bgm-manage-row structure as the index
    const row = document.createElement("div");
    row.className = "bgm-manage-row";
    row.style.marginTop = "var(--sp-3)";
    row.style.cursor = "pointer";
    row.innerHTML = `
      <button type="button" class="bgm-row__play" aria-label="Preview ${filename}">
        <svg class="icon-play" width="9" height="11" viewBox="0 0 9 11" fill="currentColor"><polygon points="0,0 9,5.5 0,11"/></svg>
        <svg class="icon-pause" width="9" height="11" viewBox="0 0 9 11" fill="currentColor" style="display:none"><rect x="0" y="0" width="3" height="11"/><rect x="6" y="0" width="3" height="11"/></svg>
      </button>
      <div class="bgm-manage-row__info">
        <div class="bgm-manage-row__name">${filename}</div>
        <div class="bgm-manage-row__meta">
          <span class="js-time">0:00</span> · ${fmt(duration)}
        </div>
      </div>
      <div class="waveform-scrubber js-waveform" hidden></div>
    `;

    if (this.hasPreviewContainerTarget) {
      this.previewContainerTarget.appendChild(row);
    }

    // Wire up WaveSurfer on the blob URL
    const waveformEl = row.querySelector(".js-waveform");
    const timeEl = row.querySelector(".js-time");
    const playEl = row.querySelector(".icon-play");
    const pauseEl = row.querySelector(".icon-pause");

    // --border is near-white in light mode — invisible as wave bars on a
    // white surface. --white never existed as a token (always fell back to
    // the hardcoded default). Use tokens that actually contrast against
    // --surface in both themes.
    const style = getComputedStyle(document.documentElement);
    const waveColor = style.getPropertyValue("--slate").trim() || "#27272a";
    const progressColor = style.getPropertyValue("--primary").trim() || "#4F46E5";
    const cursorColor = style.getPropertyValue("--text").trim() || "#a7a7a7";

    this._ws = WaveSurfer.create({
      container: waveformEl,
      url: blobUrl,
      waveColor,
      progressColor,
      cursorColor,
      cursorWidth: 2,
      barWidth: 3,
      barGap: 2,
      barRadius: 3,
      height: 48,
      normalize: true,
      interact: true,
    });

    this._blobUrl = blobUrl;
    this._ready = false;

    this._ws.on("ready", () => {
      this._ready = true;
    });

    this._ws.on("timeupdate", (t) => {
      timeEl.textContent = fmt(t);
    });

    this._ws.on("finish", () => {
      playEl.style.display = "";
      pauseEl.style.display = "none";
      waveformEl.hidden = true;
      timeEl.textContent = "0:00";
    });

    this._ws.on("interaction", () => {
      if (!this._ws.isPlaying()) {
        waveformEl.hidden = false;
        this._ws.play();
        playEl.style.display = "none";
        pauseEl.style.display = "";
      }
    });

    // Row click → toggle
    row.addEventListener("click", (e) => {
      if (e.target.closest(".waveform-scrubber")) return;
      if (!this._ready) return;

      if (this._ws.isPlaying()) {
        this._ws.pause();
        playEl.style.display = "";
        pauseEl.style.display = "none";
        waveformEl.hidden = true;
      } else {
        waveformEl.hidden = false;
        this._ws.play();
        playEl.style.display = "none";
        pauseEl.style.display = "";
      }
    });

    // Stop waveform pointerdown from bubbling to row click
    waveformEl.addEventListener("pointerdown", (e) => e.stopPropagation());
  }

  #destroyPreview() {
    if (this._ws) {
      this._ws.destroy();
      this._ws = null;
    }
    if (this._blobUrl) {
      URL.revokeObjectURL(this._blobUrl);
      this._blobUrl = null;
    }
    // Remove any dynamically injected preview rows
    if (this.hasPreviewContainerTarget) {
      this.previewContainerTarget.querySelectorAll(".bgm-manage-row").forEach((el) => {
        // Only remove dynamically added rows (no data-controller means it was injected by JS)
        if (!el.dataset.controller) el.remove();
      });
    }
  }

  #resetZoneTitle() {
    if (!this.hasZoneTitleTarget) return;
    const hasFile = this.fileInputTarget.files.length > 0;
    this.zoneTitleTarget.textContent = hasFile ? "Replace audio" : "Choose audio file";
  }
}
